import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as moment from 'moment';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Prompt } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import { PhotoViewerConstants as ui } from '../../Config/UIConstants';
import { TagType } from '../../Services/PhotoService';
import { CloseButton } from '../../SharedComponents/CloseButton';
import { ExitableAlert } from '../../SharedComponents/ExitableAlert';
import { FullContainer } from '../../SharedComponents/FullContainer';
import {
    warnBeforeDelete,
    IAcceptsDeferredLeaveAction
} from '../../SharedComponents/HigherOrderComponents/WarnsBeforeDelete';
import { NotificationsButton } from '../../SharedComponents/MainPageWithNavigator/NotificationsButton';
import { NotificationsDropdown } from '../../SharedComponents/MainPageWithNavigator/NotificationsDropDown';
import { StoryPhotoCommentsStore } from '../../Stores/StoryPhotoCommentsStore';
import { IStory, StoryViewStyle } from '../../Stores/StoryStore';
import { IUserFamily } from '../../Stores/UserFamilyStore';
import { IUserPhoto } from '../../Stores/UserPhotoStore';
import { Routes, RouteFormats } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { DEFAULT_USER_PROFILE } from '../../_stores/UserStore';
import { DropDownArrowWhite, ModalXIconInverse } from '../../images/images';
import { colors } from '../../styles/colors';
import { CarouselView } from './CarouselView';
import { CommentInput } from './CommentInput';
import { CommentTree } from './CommentTree';
import { GridView } from './GridView';
import { IconCarousel } from './IconCarousel';
import { IconFamily } from './IconFamily';
import { IconGrid } from './IconGrid';
import { IconSettings } from './IconSettings';
import { styles } from './PhotoViewerStyles';

import './PhotoViewer.css';

interface IProps extends RouteComponentProps<{ id?: string; photoID?: string }> {
    store: ApplicationStore;
}

interface IState {
    activeCommentType: CommentType;
    showFamilyMembers: boolean;
    loadingNewPhotos: boolean;
    isInitialLoad: boolean;
    isReOrderingPhotos: boolean;
    showNotificationPopOver: boolean;
    showAccountPopover: boolean;
    showSupportPopover: boolean;
    useRouterPrompt: boolean;
}
export enum CommentType {
    WRITE,
    RECORD,
}

enum Direction {
    LEFT = 'LEFT',
    RIGHT = 'RIGHT',
}



@inject('store')
@observer
class PhotoViewerClass extends React.Component<IProps & IAcceptsDeferredLeaveAction, IState> {
    public state: IState = {
        activeCommentType: CommentType.WRITE,
        showFamilyMembers: false,
        loadingNewPhotos: false,
        isInitialLoad: true,
        isReOrderingPhotos: false,
        showNotificationPopOver: false,
        showAccountPopover: false,
        showSupportPopover: false,
        useRouterPrompt: false,
    };

    private readonly tryUpdateRoutePhotoID = (index: number, story: IStory) => {
        this.navigateOrWarnIfDirty(() => {
            const photo = story.photosWithComments[index].photo;
            const photoID = photo.id;
            this.props.history.replace(Routes.PHOTO_VIEWER
                .replace(':id', story.id.toString())
                .replace(':photoID?', photoID.toString()));
            this.setState({
                activeCommentType: CommentType.WRITE
            });
        });
    }

    private readonly tryUpdateRoutePhotoIDbyID = (photoID: number, story: IStory) => {
        this.navigateOrWarnIfDirty(() => {
            this.props.history.replace(Routes.PHOTO_VIEWER
                .replace(':id', story.id.toString())
                .replace(':photoID?', photoID.toString()));
            this.setState({
                activeCommentType: CommentType.WRITE
            });
        });
    }

    private onReOrder = async (story: IStory, items: any) => {
        const postPromise = this.props.store.storyStore.reOrderPhotosInStory(story.id, items);
        await postPromise;
    }

    private toggleReorder = () => {
        this.setState({isReOrderingPhotos: !this.state.isReOrderingPhotos});
    }

    public componentDidMount(): void {
        when(
            () =>
                this.props.store.storyStore &&
                this.props.store.storyStore.storiesAreLoaded &&
                !!this.props.store.storyPhotoCommentStore,
            async () => {
                const storyId = parseInt(this.props.match.params.id as string)!;
                const story = await this.props.store.storyStore.getOrLoadStoryByID(storyId);
                const storyPhotosWithComments = story.photosWithComments;

                if (storyPhotosWithComments.length > 0) {
                    let focusIndex = -1;
                    let photoId = 0;

                    const photoIdText = this.props.match.params.photoID;
                    if (photoIdText) {
                        photoId = parseInt(photoIdText);
                        focusIndex = storyPhotosWithComments
                            .findIndex(obj => obj.photo.id === photoId);
                    }

                    focusIndex = Math.max(focusIndex, 0);
                    photoId = storyPhotosWithComments[focusIndex].photo.id;

                    await this.props.store.storyPhotoCommentStore.loadCommentsForStoryPhotoChange(storyId, photoId);
                } else {
                    this.props.history.push(Routes.ADD_PHOTOS_TO_STORY.replace(':id', story.id.toString()), {
                        emptyStory: true
                    });
                }

                this.updateStoryViewStyle(storyId);
                this.props.store.storyStore.setLastViewedStoryId(story.id);

                this.setState({ isInitialLoad: false });
            },
        );
    }

    public componentWillReceiveProps(newProps: React.ComponentProps<any>) {
        const oldPhotoID = this.props.match.params.photoID;
        const oldStoryId = this.props.match.params.id;
        const newStoryID = newProps.match.params.id;
        const newPhotoID = newProps.match.params.photoID;
        // If the photo or story ID changes, re-fetch comments
        if ((oldPhotoID !== newPhotoID) || (oldStoryId !== newStoryID)) {
            this.props.store.storyPhotoCommentStore.loadCommentsForStoryPhotoChange(newStoryID, newPhotoID)
                .catch(e => console.error('Problem loading comments', newStoryID, newPhotoID, e));
        }
    }

    public componentDidUpdate(): void {
        const storyId = parseInt(this.props.match.params.id as string)!;
        this.updateStoryViewStyle(storyId);
        this.props.store.storyStore.setLastViewedStoryId(storyId);
    }

    public componentWillUnmount(): void {
        this.props.store.storyPhotoCommentStore.deleteParticularCommentStores();
    }

    private updateStoryViewStyle(storyId: number): void {
        // If we're switching between stories, reset the view style
        if (this.props.store.storyStore.lastViewedStoryId !== storyId) {
            this.props.store.storyStore.setStoryViewStyle(StoryViewStyle.DEFAULT);
        }
    }

    private isAlbumOwner(storyCreatorID: number): boolean {
        return this.props.store.userStore.currentUser.id === storyCreatorID;
    }

    private isPhotoOwner(storyCreatorID: number): boolean {
        return this.props.store.userStore.currentUser.id === storyCreatorID;
    }

    private getCreatorName(id: number): string {
        const { userFamilyStore, userStore } = this.props.store;
        if (id === userStore.currentUser.id) {
            return userStore.currentUser.first_name;
        }
        const createdBy = userFamilyStore.getFamilyMemberByID(id);
        if (!createdBy) {
            throw new Error(`Could not find a user for id ${id}`);
        }
        return createdBy.first_name;
    }

    private navigateOrWarnIfDirty = (leaveAction: () => void): void => {
        this.props.onTryNavigate(this.props.store.storyPhotoCommentStore.hasUnsubmittedData, leaveAction);
    }

    private renderTopNavigationBar(story: IStory, photo: IUserPhoto) {
        const tagArray = [(photo.tags as any)[TagType.LOCATION], (photo.tags as any)[TagType.DATE]].filter(
            str => str !== '' && str,
        );

        console.log('tagArray', tagArray);
        const photoUploaderName = this.getCreatorName(photo.user_id);
        const createdAt = moment(photo.created_at).format('MM / DD / YYYY');
        let family: IUserFamily | undefined = undefined;
        let sharedFamilyName = '';
        let isFamilysAdmin = false;
        if (story.sharedWithFamilyID) {
            family = this.props.store.userFamilyStore.getFamilyByID(story.sharedWithFamilyID!);
            sharedFamilyName = family ? family.name : '';
            isFamilysAdmin = family && family.creator.id === this.props.store.userProfileStore.user.id;
        }
        const currentUser = this.props.store.userProfileStore.user;

        return (
            <div className={'photoViewer__topBar'}>
                <div className="photoViewer__topBar__section">
                    <div className={'photoViewer__topBar__item photoViewer__topBar__item--title'}>
                        <h1 data-tip={story.title} className={'photoViewer__title'}>{story.title}</h1>
                        <ReactTooltip place="bottom" type="dark" effect="solid"/>
                        <p className={'photoViewer__attribution'}>Created By: <strong>{this.getCreatorName(story.creator_id)}</strong></p>
                    </div>
                    <div className={'photoViewer__topBar__item'}>
                        <StoryTag
                            title={ui.ADD_PHOTO}
                            background={colors.ACTION_GREEN}
                            onClick={() =>
                                this.navigateOrWarnIfDirty(() =>
                                    this.props.history.push(
                                        Routes.ADD_PHOTOS_TO_STORY.replace(':id', story.id.toString()),
                                        { cameFromPhotoViewer: true },
                                    ),
                                )
                            }
                        />
                    </div>
                </div>



                <div className="photoViewer__topBar__section">
                    {this.isAlbumOwner(story.creator_id) && (
                        <a
                            onClick={() =>
                                this.navigateOrWarnIfDirty(() =>
                                    this.props.history.push(
                                        RouteFormats.storySettings(parseInt(String(story.id))),
                                        {cameFromPhotoViewer: true}
                                    ),
                                )
                            }
                            className="photoViewer__topBar__item photoViewer__topBar__item--settings">
                            <IconSettings style={{marginRight: 10}}  />
                            Album Settings
                        </a>
                    )}
                    <div>
                        {family ? (
                            <div className={'photoViewer__topBar__item photoViewer__topBar__item--family'}>
                                <IconFamily style={{marginRight: 10}}  />

                                <span style={styles.sharedWithText}>{ui.SHARED_WITH}:</span>
                                <span
                                    onClick={() => this.setState({ showFamilyMembers: true })}
                                    style={styles.familyName}
                                >
                                    {sharedFamilyName}
                                    <img src={DropDownArrowWhite} style={{marginLeft: 10}}  />

                                    {this.state.showFamilyMembers && (
                                        <div style={styles.familyMemberDropdown}>
                                            {family.members.map((user, index) => {
                                                return (
                                                    <p key={index} style={styles.familyMemberName}>
                                                        {this.props.store.userFamilyStore.getFamilyMemberFullName(user)}
                                                    </p>
                                                );
                                            })}
                                            {isFamilysAdmin && (
                                                <p
                                                    style={styles.addFamilyMemberButton}
                                                    onClick={() =>
                                                        this.navigateOrWarnIfDirty(() =>
                                                            this.props.history.push(
                                                                Routes.ADD_FAMILY_MEMBER.replace(
                                                                    ':id',
                                                                    family!.id.toString(),
                                                                ),
                                                                {
                                                                    cameFromStoryID: parseInt(this.props.match.params.id as string),
                                                                },
                                                            )
                                                        )
                                                    }
                                                >
                                                    {ui.ADD_FAMILY_MEMBER}
                                                </p>
                                            )}

                                            <div style={styles.bottomPadding} />
                                        </div>
                                    )}
                                </span>
                            </div>
                        ) : (
                            <p>
                                <span style={styles.sharedWithText}>{ui.PRIVATE_STORY}</span>
                            </p>
                        )}
                    </div>

                    <div className={'photoViewer__topBar__item'}>
                        <NotificationsButton
                            onClick={() => this.setState({
                                showNotificationPopOver: !this.state.showNotificationPopOver,
                                showAccountPopover: false,
                                showSupportPopover: false,
                                useRouterPrompt: true,
                            })}
                        />
                        {this.state.showNotificationPopOver && (
                            <NotificationsDropdown
                                style={{right: 18}}
                                onOverlayClick={
                                () => {
                                    this.setState({
                                        showNotificationPopOver: false,
                                        useRouterPrompt: false,
                                    });
                                }
                            } />
                        )}
                    </div>

                    <div className={'photoViewer__topBar__item photoViewer__topBar__item--close'}>
                        <CloseButton
                            dark={true}
                            onClick={() =>
                                this.navigateOrWarnIfDirty(() =>
                                    this.props.history.push(Routes.ALBUMS)
                                )
                            }
                        />
                    </div>

                    {this.state.showFamilyMembers && (
                        <div
                            onClick={() => this.setState({ showFamilyMembers: false })}
                            style={styles.dropDownBackground}
                        />
                    )}

                </div>

            </div>
        );
    }

    private renderPhotoMeta(story: IStory, photo: IUserPhoto, photoIndex: number) {
        let user_info = DEFAULT_USER_PROFILE;
        if (photo.user_info) {
            user_info = photo.user_info;
        } else if (photo.user_id === this.props.store.userStore.currentUser.id) {
            user_info = this.props.store.userStore.currentUser;
        }
        let photoMeta = {
            location: photo.tags ? (photo.tags as any)[TagType.LOCATION] : null,
            date: photo.tags ? (photo.tags as any)[TagType.DATE] : null,
            createdAt: moment(photo.created_at).format('MM/DD/YYYY'),
            user_info,
        };
        return (
            <div className="photoViewer__meta">
                <span className={'photoViewer__meta__position'}>
                    ({photoIndex + 1} of {story.photosWithComments.length})
                </span>
                {(photoMeta.location || photoMeta.date) && (
                    <span>&nbsp;&ndash;</span>
                )}
                {photoMeta.location && (
                    <span className={'photoViewer__meta__location'}>&nbsp;{photoMeta.location}</span>
                )}
                {photoMeta.date && (
                    <span className={'photoViewer__meta__date'}>&nbsp;{photoMeta.date}</span>
                )}
                <span className={'photoViewer__meta__attribution'}>
                    Added By: <span className={'photoViewer__meta__user'}>{photoMeta.user_info.first_name} {photoMeta.user_info.last_name}</span> <span>{photoMeta.createdAt}</span>
                </span>
            </div>
        );
    }

    private handleAddComment = async (storyId: number, currentPhotoId: number) => {
        const postPromise = this.props.store.storyPhotoCommentStore.postComment(storyId, currentPhotoId);
        this.setState({
            activeCommentType: CommentType.WRITE
        });
        await postPromise;
    }

    private onClickArrow = (direction: Direction, story: IStory, currentPhotoIndex: number): void => {
        let index = 0;
        if (direction === Direction.RIGHT) {
            index = (currentPhotoIndex + 1) % story.photosWithComments.length;
        } else {
            index =
                (currentPhotoIndex + story.photosWithComments.length - 1) %
                story.photosWithComments.length;
        }

        this.tryUpdateRoutePhotoID(index, story);
    }

    private onClickCommentTab(commentType: CommentType): void {
        // We can't rely on react-router's <Prompt> for this because the route isn't changing
        this.navigateOrWarnIfDirty(() => {
            this.props.store.storyPhotoCommentStore.enableTopLevelCommentEditor();
            this.setState({ activeCommentType: commentType });
        });
    }

    public render() {
        if (this.state.isInitialLoad) {
            return null;
        }

        const storyId = parseInt(this.props.match.params.id as string)!;
        const story = this.props.store.storyStore.getStoryByID(storyId);
        const { storyViewStyle } = this.props.store.storyStore;
        console.log('story', story);

        const { photoID: photoIdText } = this.props.match.params;
        let photoIndex = -1;
        if (photoIdText) {
            const parsed = parseInt(photoIdText);
            photoIndex = story.photosWithComments.findIndex(storyPhoto => storyPhoto.photo.id === parsed);
        }
        if (photoIndex < 0) {
            photoIndex = 0;
        }

        const photo = story.photosWithComments[photoIndex].photo;
        const photoId = photo.id;

        const commentsKey = StoryPhotoCommentsStore.formatStoryAndPhotoIdKey(story.id, photoId);
        const commentsForStoryAndPhoto =
            this.props.store.storyPhotoCommentStore.commentsByPhotoAndStoryID.get(commentsKey) || [];

        const areRepliesClosed = this.props.store.storyPhotoCommentStore.replyTargetId === undefined;

        const { activeCommentType } = this.state;

        return (
            <FullContainer style={styles.container}>
                {this.renderTopNavigationBar(story, photo)}

                <div style={styles.fullContainer}>
                    <div className={'photoViewer__main'}>

                        {this.renderPhotoMeta(story, photo, photoIndex)}

                        <div className={'photoViewer__controls'}>

                            {this.isPhotoOwner(photo.user_id) && (
                                <div className={'photoViewer__control photoViewer__control--btn'}>
                                    <StoryTag
                                        title={'Edit Photo'}
                                        background={colors.ACTION_GREEN}
                                        onClick={() =>
                                            this.navigateOrWarnIfDirty(() =>
                                                this.props.history.push(
                                                    Routes.PHOTO_DETAIL.replace(':id', photo.id.toString()),
                                                    { cameFromStory: true },
                                                ),
                                            )
                                        }
                                    />
                                </div>
                            )}

                            {storyViewStyle === StoryViewStyle.GRID && this.isAlbumOwner(story.creator_id) && (
                                <div className={'photoViewer__control'}>
                                    <label className="customCheckbox">
                                        Re-arrange Photos
                                        <input
                                            type="checkbox"
                                            checked={this.state.isReOrderingPhotos}
                                            onChange={this.toggleReorder}
                                        />
                                        <span className="customCheckbox__check" />
                                    </label>
                                </div>
                            )}

                            <button className={'photoViewer__control'}
                                    onClick={() => {
                                        this.props.store.storyStore.setStoryViewStyle(StoryViewStyle.GRID);
                                    }}
                            >
                                <IconGrid active={storyViewStyle === StoryViewStyle.GRID ? 1 : 0}/>
                            </button>

                            <button className={'photoViewer__control'}
                                 onClick={() => {
                                     this.setState({isReOrderingPhotos: false});
                                     this.props.store.storyStore.setStoryViewStyle(StoryViewStyle.CAROUSEL);
                                 }}>
                                <IconCarousel active={storyViewStyle === StoryViewStyle.CAROUSEL ? 1 : 0} />
                            </button>

                        </div>

                        {storyViewStyle === StoryViewStyle.CAROUSEL && (
                            <CarouselView
                                story={story}
                                photo={photo}
                                selectedIndex={photoIndex}
                                onClickLeft={() => this.onClickArrow(Direction.LEFT, story, photoIndex)}
                                onClickRight={() => this.onClickArrow(Direction.RIGHT, story, photoIndex)}
                                tryUpdateRoutePhotoID={this.tryUpdateRoutePhotoID}
                            />
                        )}

                        {storyViewStyle === StoryViewStyle.GRID && (
                            <GridView
                                story={story}
                                photo={photo}
                                onReOrder={this.onReOrder}
                                selectedId={photoId}
                                isReOrderingPhotos={this.state.isReOrderingPhotos}
                                tryUpdateRoutePhotoIDbyID={this.tryUpdateRoutePhotoIDbyID}
                                location={this.props.location}
                            />
                        )}
                    </div>
                    <div style={styles.rightSide}>
                        <div style={{...styles.commentTypeTabs, display: this.state.isReOrderingPhotos ? 'none' : 'flex'}}>
                            {/* ToDo: Move the tabs into comment input.
                                      Keep activeCommentType as local component state.
                                      Re-use shared component for both top-level comments and replies with slightly different styling */}
                            <div
                                className={activeCommentType === CommentType.WRITE ? 'photoViewer__tab active' : 'photoViewer__tab'}
                                style={styles.commentTypeContainer}
                                onClick={() => this.onClickCommentTab(CommentType.WRITE)}
                            >
                                <p className={'photoViewer__tab__text'}>{ui.WRITE}</p>
                            </div>

                            <div
                                className={activeCommentType === CommentType.RECORD ? 'photoViewer__tab active' : 'photoViewer__tab'}
                                style={styles.commentTypeContainer}
                                onClick={() => this.onClickCommentTab(CommentType.RECORD)}
                            >
                                <p className={'photoViewer__tab__text'}>{ui.RECORD}</p>
                            </div>
                        </div>

                        <div style={{...styles.commentInput, display: this.state.isReOrderingPhotos ? 'none' : 'flex'}}>
                            <Prompt
                                when={this.props.store.storyPhotoCommentStore.hasUnsubmittedData && this.state.useRouterPrompt}
                                message={'You didn’t ‘Add’ your comment. Click ‘Cancel’ to go back and add it, otherwise your comment will be deleted.'}
                            />

                            {areRepliesClosed && (
                                <CommentInput
                                    activeCommentType={activeCommentType}
                                    onAddComment={() => this.handleAddComment(story.id, photoId)}
                                    commentEditState={this.props.store.storyPhotoCommentStore.topLevelCommentEditState!}
                                />
                            )}
                        </div>
                        <CommentTree
                            comments={[...commentsForStoryAndPhoto]}
                        />
                    </div>
                </div>

                {this.state.loadingNewPhotos && <ExitableAlert headerText={ui.LOADING} />}

                {this.state.showNotificationPopOver && (
                    <div
                        onClick={() => this.setState({showNotificationPopOver: false}) }
                        style={styles.dropDownBackground} />
                )}
            </FullContainer>
        );
    }
}

export const PhotoViewer = warnBeforeDelete(PhotoViewerClass);

const StoryTag = (props: { title: string; onClick?(): void; background?: string }) => {
    return (
        <div
            style={{
                ...styles.storyTag,
                ...(props.background ? { backgroundColor: props.background } : { backgroundColor: '#CB6C3C' }),
                ...(props.onClick ? styles.pointerCursor : {}),
            }}
            onClick={props.onClick}
        >
            <p style={styles.tagText}>{props.title}</p>
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/Pages/PhotoViewer/PhotoViewer.tsx